import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import {
  fetchClients,
  issueClientToken,
  updateClient,
  type ClientToken,
  type WorkHoursClientConfig,
  type WorkHoursClientRow,
} from "../services/workHoursService";
import { describeRequestFailure, type FailureKeys } from "../lib/requestFailure";
import { buildThemeClasses, inputClass } from "../theme";
import ClientEconomicsCard from "../components/ClientEconomicsCard";

// ---------------------------------------------------------------------------
// Clientes del reporte semanal (#539): los repos que les pertenecen y a quién
// le llega el reporte. Cada viernes reciben solo sus repos, sin nombres de devs.
//
// Los clientes SON las Empresas del CRM en etapa Cliente (#973): aquí no se dan
// de alta, ni se renombran, ni se borran —eso vive en CRM → Empresas—. Solo se
// configura lo de horas (repos, correos y si el reporte está activo).
//
// Solo dueños (#1064): la sección va envuelta en `OwnersOnly` (config.tsx) y
// arriba de la tabla muestra el % del tiempo, el costo, el ingreso y la ganancia
// de cada cliente (`ClientEconomicsCard`).
//
// El error no inventa su causa: cualquier tropiezo al cargar se leía como
// «solo los administradores gestionan clientes», y un corte de red no es un
// problema de permisos. Eso solo lo puede decir un 403.
// ---------------------------------------------------------------------------

const split = (v: string) => v.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
const empty = { id: "", name: "", emails: "", repos: "", active: true, shareTeam: false };

/**
 * CRM → Contactos y Leads → Empresas (`CRM_BASE` + slug + item de
 * `Layouts/crm/config.tsx`). Se escribe tal cual para no arrastrar el CRM
 * entero al bundle de Horas Trabajadas.
 */
export const CRM_COMPANIES_PATH = "/crm/contactos/empresas";

/** Error a mostrar: o una clave i18n (`key`) o el texto tal cual lo manda la API (`text`). */
type SectionError = { key?: string; text?: string } | null;

/** Fallo al CARGAR la lista. */
const LOAD_KEYS: FailureKeys = {
  failed: "workHours.clients.loadError",
  permission: "workHours.clients.adminOnly",
  auth: "workHours.clients.authError",
  unconfirmed: "workHours.clients.loadTimeout",
};

/** Fallo al configurar o pausar/reanudar el reporte de un cliente. */
const SAVE_KEYS: FailureKeys = {
  failed: "workHours.clients.saveError",
  permission: "workHours.clients.saveForbidden",
  auth: "workHours.clients.authError",
  unconfirmed: "workHours.clients.saveUnconfirmed",
};

/**
 * Traduce el fallo a lo que la sección puede afirmar. El motivo del servidor
 * gana —es el único que alguien verificó—; si no lo hay, manda el código, y de
 * permisos solo se habla con un 403.
 */
const sectionError = (error: unknown, keys: FailureKeys): SectionError => {
  const failure = describeRequestFailure(error);
  if (failure.serverMessage) return { text: failure.serverMessage };
  if (failure.kind === "permission") return { key: keys.permission };
  if (failure.kind === "auth") return { key: keys.auth };
  if (failure.kind === "unconfirmed") return { key: keys.unconfirmed };
  return { key: keys.failed };
};

export default function ClientsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const card = `rounded-2xl border p-4 ${tc.cardBg} ${tc.border}`;

  const [rows, setRows] = useState<WorkHoursClientRow[]>([]);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState<SectionError>(null);
  // Token de cliente recién emitido (#977): se muestra UNA vez y no se guarda.
  const [issued, setIssued] = useState<(ClientToken & { clientName: string }) | null>(null);
  const [copied, setCopied] = useState(false);

  // `load` NO depende de `t`: react-i18next devuelve una `t` nueva al cargar un
  // namespace o al cambiar de idioma, y eso recargaba la lista sin motivo. El
  // error se guarda como clave y se traduce al pintar.
  const load = useCallback(async () => {
    try {
      setRows(await fetchClients());
      setError(null);
    } catch (err) {
      setError(sectionError(err, LOAD_KEYS));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const errorText = error ? (error.key ? t(error.key) : error.text) : null;

  const save = async (id: string, payload: WorkHoursClientConfig) => {
    try {
      await updateClient(id, payload);
      await load();
      return true;
    } catch (err) {
      const failure = describeRequestFailure(err);
      // Sin respuesta el cambio pudo haber quedado hecho: se relee la lista
      // antes de opinar, para que se vea el estado real junto al aviso.
      if (failure.kind === "unconfirmed") await load();
      setError(sectionError(err, SAVE_KEYS));
      return false;
    }
  };

  const submit = async () => {
    if (!form.id) return;
    const payload = { emails: split(form.emails), repos: split(form.repos), active: form.active, shareTeam: form.shareTeam };
    if (await save(form.id, payload)) setForm(empty);
  };

  const edit = (c: WorkHoursClientRow) =>
    setForm({ id: c.id, name: c.name, emails: c.emails.join(", "), repos: c.repos.join(", "), active: c.configured ? c.active !== false : true, shareTeam: c.shareTeam === true });

  // Pausar / reanudar sin borrar: el cliente pausado deja de recibir el
  // reporte semanal pero conserva su histórico de entregas.
  const toggleActive = (c: WorkHoursClientRow) =>
    save(c.id, { emails: c.emails, repos: c.repos, active: c.active === false, shareTeam: c.shareTeam === true });

  // Token de solo lectura para la app del cliente (#977). Lo configura quien
  // despliega esa app como secreto de su backend; aquí solo se muestra una vez.
  const issueToken = async (c: WorkHoursClientRow) => {
    try {
      setCopied(false);
      const token = await issueClientToken(c.id, `Vista de cliente · ${c.name}`);
      setIssued({ ...token, clientName: c.name });
      setError(null);
    } catch (err) {
      setError(sectionError(err, SAVE_KEYS));
    }
  };

  const copyToken = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="w-full max-w-5xl overflow-y-auto p-4 sm:p-6 space-y-4">
      <div>
        <h1 className={`text-xl font-bold ${tc.text}`}>{t("workHours.menu.clients")}</h1>
        <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.clients.subtitle")}</p>
      </div>
      {errorText && (
        <p data-testid="clients-error" className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
          {errorText}
        </p>
      )}

      <div data-testid="clients-from-crm" className={`${card} flex flex-wrap items-center justify-between gap-3 text-sm ${tc.textSecondary}`}>
        <span>
          <i className="ri-building-line mr-1" />
          {t("workHours.clients.fromCrm")}
        </span>
        <Link to={CRM_COMPANIES_PATH} className="rounded-lg border border-violet-500/40 px-3 py-1.5 text-sm font-medium text-violet-500">
          {t("workHours.clients.openCrm")}
        </Link>
      </div>

      {form.id && (
        <div data-testid="client-config-form" className={card}>
          <p className={`mb-3 font-medium ${tc.text}`}>
            {t("workHours.clients.configure")} · {form.name}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input aria-label={t("workHours.clients.emails")} placeholder="cto@cliente.mx" value={form.emails} onChange={(e) => setForm({ ...form, emails: e.target.value })} className={inputClass(isDarkMode)} />
            <input aria-label={t("workHours.clients.repos")} placeholder="Coding-Cloud-Online/NUVEX" value={form.repos} onChange={(e) => setForm({ ...form, repos: e.target.value })} className={inputClass(isDarkMode)} />
          </div>
          <label className={`mt-3 flex items-center gap-2 text-sm ${tc.textSecondary}`}>
            <input
              type="checkbox"
              aria-label={t("workHours.clients.active")}
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="h-4 w-4 accent-violet-600"
            />
            {t("workHours.clients.active")}
            <span className="text-xs opacity-70">· {t("workHours.clients.activeHint")}</span>
          </label>
          <label className={`mt-2 flex items-center gap-2 text-sm ${tc.textSecondary}`}>
            <input
              type="checkbox"
              aria-label={t("workHours.clients.shareTeam")}
              checked={form.shareTeam}
              onChange={(e) => setForm({ ...form, shareTeam: e.target.checked })}
              className="h-4 w-4 accent-violet-600"
            />
            {t("workHours.clients.shareTeam")}
            <span className="text-xs opacity-70">· {t("workHours.clients.shareTeamHint")}</span>
          </label>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={submit} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {t("workHours.save")}
            </button>
            <button type="button" onClick={() => setForm(empty)} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary}`}>
              {t("workHours.cancel")}
            </button>
          </div>
        </div>
      )}

      {issued && (
        <div data-testid="client-token" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
          <p className={`font-medium ${tc.text}`}>{t("workHours.clients.tokenTitle", { name: issued.clientName })}</p>
          <p className={`mt-1 text-xs ${tc.textSecondary}`}>{t("workHours.clients.tokenHint")}</p>
          <div className="mt-2 flex items-center gap-2">
            <code className={`flex-1 break-all rounded-lg border p-2 text-[11px] ${tc.border} ${tc.text}`}>{issued.token}</code>
            <button type="button" onClick={() => void copyToken()} className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white">
              {copied ? t("workHours.clients.tokenCopied") : t("workHours.clients.tokenCopy")}
            </button>
            <button type="button" aria-label="close-token" onClick={() => setIssued(null)} className={`rounded-lg border px-3 py-2 text-xs ${tc.border} ${tc.textSecondary}`}>
              {t("workHours.cancel")}
            </button>
          </div>
        </div>
      )}

      <ClientEconomicsCard />

      <div className={card}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left text-xs ${tc.textSecondary}`}>
              <th className="py-1">{t("workHours.clients.name")}</th>
              <th className="py-1">{t("workHours.clients.emails")}</th>
              <th className="py-1">{t("workHours.clients.repos")}</th>
              <th className="py-1">{t("workHours.clients.status")}</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const isActive = c.configured === true && c.active !== false;
              const status = c.lockedByOtherCompany
                ? { key: "workHours.clients.lockedOther", tone: "bg-gray-400/15 text-gray-500" }
                : !c.configured
                  ? { key: "workHours.clients.notConfigured", tone: "bg-amber-500/15 text-amber-500" }
                  : isActive
                    ? { key: "workHours.clients.active", tone: "bg-emerald-500/15 text-emerald-500" }
                    : { key: "workHours.clients.inactive", tone: "bg-gray-400/15 text-gray-500" };
              return (
              <tr key={c.id} className={`border-t ${tc.border} ${tc.text} ${isActive ? "" : "opacity-60"}`}>
                <td className="py-1.5 font-medium">{c.name}</td>
                <td className="py-1.5 text-xs">{c.emails.join(", ")}</td>
                <td className="py-1.5 text-xs">{c.repos.join(", ")}</td>
                <td className="py-1.5">
                  <span data-testid={`status-${c.id}`} className={`rounded-full px-2 py-0.5 text-[11px] ${status.tone}`}>
                    {t(status.key)}
                  </span>
                </td>
                <td className="py-1.5 text-right whitespace-nowrap">
                  {c.configured && (
                    <button
                      type="button"
                      aria-label={`${isActive ? "deactivate" : "activate"}-${c.id}`}
                      title={isActive ? t("workHours.clients.deactivate") : t("workHours.clients.activate")}
                      onClick={() => void toggleActive(c)}
                      className={`mr-2 ${isActive ? "text-amber-500" : "text-emerald-500"}`}
                    >
                      <i className={isActive ? "ri-pause-circle-line" : "ri-play-circle-line"} />
                    </button>
                  )}
                  {isActive && (
                    <button
                      type="button"
                      aria-label={`token-${c.id}`}
                      title={t("workHours.clients.issueToken")}
                      onClick={() => void issueToken(c)}
                      className="mr-2 text-sky-500"
                    >
                      <i className="ri-key-2-line" />
                    </button>
                  )}
                  {!c.lockedByOtherCompany && (
                    <button type="button" aria-label={`edit-${c.id}`} title={t("workHours.clients.configure")} onClick={() => edit(c)} className="text-violet-500">
                      <i className="ri-settings-3-line" />
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className={`py-3 text-center text-xs ${tc.textSecondary}`}>
                  {t("workHours.clients.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
